import React from "react"
import {
  BillingProductName,
  useCheckBillingOrganizationPlanQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { isOrgNonSandboxAccountingFirm } from "@digits-shared/helpers/organizationHelper"
import { type PathGenerator } from "@digits-shared/hooks/usePathGenerator"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import { JWTPermissionFlag } from "@digits-shared/session/jwt/jwtPermissions"
import { ONBOARDING_FIRM_ORG_SLUG_PARAM } from "src/frontend/components/Onboard/PLG/Firm/route"
import { useHasPermission } from "src/frontend/components/Shared/Permissions/Requires"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"

export function useOnAddClient() {
  const { currentOrganization } = useSession<FrontendSession>()
  const { history } = useRouter()

  const canRead = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.Billing,
    flag: JWTPermissionFlag.Read,
  })
  const canCreateLegalEntity = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.LegalEntities,
    flag: JWTPermissionFlag.Create,
  })

  const { data, loading, error } = useCheckBillingOrganizationPlanQuery({
    variables: {
      organizationId: currentOrganization?.id || "",
    },
    skip:
      !currentOrganization?.id ||
      !canRead ||
      !canCreateLegalEntity ||
      !isOrgNonSandboxAccountingFirm(currentOrganization),
  })

  const didResolve = !!data?.checkBillingOrganizationPlan
  const didError = !!error
  const productName =
    data?.checkBillingOrganizationPlan.billingPlan?.product.name || BillingProductName.UnknownName

  const canAddClient = useCanAddClient(didResolve, didError, productName)
  const addClientPath = useAddClientPath(productName, canAddClient)

  const onAddClient = React.useCallback(() => {
    if (!addClientPath) return
    history.push(addClientPath)
  }, [history, addClientPath])

  return React.useMemo(
    () => ({
      loading,
      onAddClient,
      addClientPath,
      canAddClient,
      productName,
    }),
    [loading, onAddClient, addClientPath, canAddClient, productName]
  )
}

/**
 * Generates a path to the legacy Add Client flow
 */
export function useLegacyAddClientPath() {
  const { currentOrganization } = useSession<FrontendSession>()
  const generatePath = useFrontendPathGenerator()

  return currentOrganization?.slug
    ? makeLegacyAddClientPath(generatePath, currentOrganization.slug)
    : ""
}

/**
 * Generates a path to the correct Add Client flow given the productName
 * If canAddClient is false an empty string is returned
 */
function useAddClientPath(productName: BillingProductName, canAddClient: boolean) {
  const { currentOrganization } = useSession<FrontendSession>()
  const orgSlug = currentOrganization?.slug
  const generatePath = useFrontendPathGenerator()
  return React.useMemo(() => {
    if (!canAddClient || !orgSlug) {
      return ""
    }

    if (productName === BillingProductName.ProspectFirm) {
      return generatePath(routes.firmAddNewClient, {
        [ONBOARDING_FIRM_ORG_SLUG_PARAM]: orgSlug,
      })
    }

    return makeLegacyAddClientPath(generatePath, orgSlug)
  }, [generatePath, orgSlug, productName, canAddClient])
}

/*
 * A lightweight check intended to prevent showing the "Add Client" button to users that shouldn't see it.
 * Notably this does not check the legacy plan limit, as that check is guaranteed within the /welcome flow.
 */
function useCanAddClient(didResolve: boolean, didError: boolean, productName: BillingProductName) {
  const { currentOrganization } = useSession<FrontendSession>()
  const canOrgAddClient = isOrgNonSandboxAccountingFirm(currentOrganization)
  const hasBillingClientsPermission = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.BillingClients,
    flag: JWTPermissionFlag.Update,
  })
  const canCreateLegalEntity = useHasPermission({
    source: FrontendPermissionSource.Organization,
    module: FrontendPermissionModule.LegalEntities,
    flag: JWTPermissionFlag.Create,
  })

  if (!didResolve || didError || !canCreateLegalEntity || !canOrgAddClient) {
    return false
  }

  if (productName === BillingProductName.ProspectFirm) {
    return hasBillingClientsPermission
  }

  return true
}

function makeLegacyAddClientPath(generatePath: PathGenerator, orgSlug: string) {
  return generatePath(routes.welcome, { org: orgSlug })
}
